<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>策略路由 Rule 管理</strong>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openCreateModal">新建策略</CButton>
            <CButton color="success" size="sm" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <CAlert color="info" fade>
          通过 <code>/routing/rule</code> 策略路由规则，按源地址/目的地址控制流量走指定的路由表。
          默认 action 为 <code>lookup-only-in-table</code>，也支持 lookup / unreachable / drop。
          创建/编辑/删除时立即下发到路由器，失败则不保存。路由表列表由 agent 定期同步。
        </CAlert>

        <CDataTable
          :items="policyList"
          :fields="fields"
          hover
          striped
          :loading="loading"
          sorter
          pagination
          :items-per-page="10"
        >
          <template #match="{ item }">
            <td class="align-middle">
              <span v-if="item.src_address" class="badge badge-light mr-1" style="font-size: 14px;">src: {{ item.src_address }}</span>
              <span v-if="item.dst_address" class="badge badge-light" style="font-size: 14px;">dst: {{ item.dst_address }}</span>
              <span v-if="!item.src_address && !item.dst_address" class="text-muted">-</span>
            </td>
          </template>
          <template #action_type="{ item }">
            <td class="text-center align-middle">
              <CBadge :color="actionColor(item.action)" style="font-size: 14px;">{{ item.action }}</CBadge>
              <div v-if="item.table_name" class="text-muted mt-1" style="font-size: 14px;">→ {{ item.table_name }}</div>
            </td>
          </template>
          <template #enabled="{ item }">
            <td class="text-center align-middle">
              <CBadge :color="item.enabled ? 'success' : 'secondary'">
                {{ item.enabled ? '启用' : '禁用' }}
              </CBadge>
            </td>
          </template>
          <template #router="{ item }">
            <td class="align-middle">
              <span v-if="item.router">{{ item.router.name }} ({{ item.router.host }})</span>
              <span v-else class="text-muted">未关联</span>
            </td>
          </template>
          <template #action="{ item }">
            <td class="text-center align-middle">
              <CButtonGroup>
                <CButton color="primary" size="sm" @click="openEditModal(item)">编辑</CButton>
                <CButton color="warning" size="sm" @click="togglePolicy(item)">{{ item.enabled ? '禁用' : '启用' }}</CButton>
                <CButton color="danger" size="sm" @click="deletePolicy(item)">删除</CButton>
              </CButtonGroup>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <!-- 创建/编辑 Modal -->
    <CModal :show.sync="formModal.show" :title="formModal.isEdit ? '编辑策略' : '新建策略'" color="primary" size="xl">
      <CForm>
        <CRow>
          <CCol sm="8">
            <CInput
              label="策略名称"
              v-model="formModal.form.name"
              placeholder="如 to-qcloud-de1-via-main"
            />
          </CCol>
          <CCol sm="4">
            <CSelect
              label="关联路由器"
              v-model="formModal.form.router_id"
              :options="routerOptions"
              placeholder="请选择路由器"
              @change="onRouterChange"
            />
          </CCol>
        </CRow>

        <CTextarea label="描述" v-model="formModal.form.description" :rows="2" />

        <CRow>
          <CCol sm="6">
            <CInput
              label="源地址 (src-address)"
              v-model="formModal.form.src_address"
              placeholder="如 192.168.1.0/24 或 10.0.0.1（可空）"
            />
          </CCol>
          <CCol sm="6">
            <CInput
              label="目的地址 (dst-address)"
              v-model="formModal.form.dst_address"
              placeholder="如 8.8.8.8/32 或 0.0.0.0/0（可空）"
            />
          </CCol>
        </CRow>
        <CAlert v-if="!formModal.form.src_address && !formModal.form.dst_address" color="warning" fade>
          源地址和目的地址至少填写一个
        </CAlert>

        <CRow>
          <CCol sm="4">
            <CSelect
              label="Action"
              v-model="formModal.form.action"
              :options="actionOptions"
            />
          </CCol>
          <CCol sm="4" v-if="formModal.form.action === 'lookup-only-in-table' || formModal.form.action === 'lookup'">
            <CSelect
              v-if="tableOptions.length > 0"
              label="目标路由表 (table)"
              v-model="formModal.form.table_name"
              :options="tableOptions"
              placeholder="请选择路由表"
            />
            <CInput
              v-else
              label="目标路由表 (table)"
              v-model="formModal.form.table_name"
              placeholder="无缓存，手动输入表名"
              size="sm"
            />
          </CCol>
          <CCol sm="4">
            <CSelect
              label="启用状态"
              v-model="formModal.form.enabled"
              :options="[
                { value: 1, label: '启用' },
                { value: 0, label: '禁用' }
              ]"
            />
          </CCol>
        </CRow>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="formModal.show = false">取消</CButton>
          <CButton color="success" :disabled="submitting" @click="submitForm">
            {{ submitting ? '下发中...' : (formModal.isEdit ? '保存' : '创建') }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'RoutingRuleList',
  data() {
    return {
      loading: false,
      submitting: false,
      allPolicies: [],
      allRouters: [],
      cachedTables: [],
      fields: [
        { key: 'name', label: '策略名称', _style: 'min-width:160px; width:15%' },
        { key: 'match', label: '匹配条件', _style: 'width:22%' },
        { key: 'action_type', label: 'Action / Table', _style: 'width:18%' },
        { key: 'router', label: '关联路由器', _style: 'width:20%' },
        { key: 'enabled', label: '状态', _style: 'width:8%' },
        { key: 'action', label: '操作', _style: 'width:17%' }
      ],
      actionOptions: [
        { value: 'lookup-only-in-table', label: 'lookup-only-in-table（仅查指定表）' },
        { value: 'lookup', label: 'lookup（查指定表，查不到再查主表）' },
        { value: 'unreachable', label: 'unreachable（不可达，回复 ICMP）' },
        { value: 'drop', label: 'drop（静默丢弃）' }
      ],
      formModal: {
        show: false,
        isEdit: false,
        editId: null,
        form: this.emptyForm()
      },
      confirmModal: {
        show: false,
        title: '确认',
        message: '',
        callback: () => {}
      }
    }
  },
  computed: {
    policyList() {
      return this.allPolicies
    },
    routerOptions() {
      return this.allRouters.map(r => ({
        value: r.id,
        label: `${r.name} (${r.host})`
      }))
    },
    tableOptions() {
      return this.cachedTables.map(t => ({
        value: t.name,
        label: t.name + (t.is_fib ? ' (fib)' : '')
      }))
    }
  },
  mounted() {
    this.loadList()
    this.loadAllRouters()
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        description: '',
        src_address: '',
        dst_address: '',
        action: 'lookup-only-in-table',
        table_name: '',
        enabled: 1,
        router_id: ''
      }
    },
    actionColor(action) {
      const map = {
        'lookup-only-in-table': 'primary',
        'lookup': 'info',
        'unreachable': 'danger',
        'drop': 'dark'
      }
      return map[action] || 'secondary'
    },
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/routing/rule/policies')
        if (res.data.code === 200) {
          this.allPolicies = res.data.data
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    openCreateModal() {
      this.formModal.form = this.emptyForm()
      this.formModal.isEdit = false
      this.formModal.editId = null
      this.cachedTables = []
      this.formModal.show = true
    },
    openEditModal(item) {
      this.formModal.form = {
        name: item.name,
        description: item.description || '',
        src_address: item.src_address || '',
        dst_address: item.dst_address || '',
        action: item.action,
        table_name: item.table_name || '',
        enabled: item.enabled ? 1 : 0,
        router_id: item.router_id || ''
      }
      this.formModal.isEdit = true
      this.formModal.editId = item.id
      this.cachedTables = []
      this.formModal.show = true
      if (this.formModal.form.router_id) {
        this.loadCachedTables(this.formModal.form.router_id)
      }
    },
    onRouterChange() {
      this.cachedTables = []
      this.formModal.form.table_name = ''
      if (this.formModal.form.router_id) {
        this.loadCachedTables(this.formModal.form.router_id)
      }
    },
    async loadCachedTables(routerId) {
      try {
        const res = await this.$axios.get(`user/routing/rule/routers/${routerId}/tables`)
        if (res.data.code === 200) {
          this.cachedTables = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    normalizeAddress(addr) {
      if (!addr) return ''
      addr = addr.trim()
      if (!addr) return ''
      if (addr.includes('/')) return addr
      // IPv6 含冒号，补 /128；IPv4 补 /32
      return addr + (addr.includes(':') ? '/128' : '/32')
    },
    async submitForm() {
      const f = { ...this.formModal.form }
      if (!f.name) {
        this.$toasted.show('策略名称必填', { type: 'error' })
        return
      }
      f.src_address = this.normalizeAddress(f.src_address)
      f.dst_address = this.normalizeAddress(f.dst_address)
      if (!f.src_address && !f.dst_address) {
        this.$toasted.show('源地址和目的地址至少填一个', { type: 'error' })
        return
      }
      if ((f.action === 'lookup-only-in-table' || f.action === 'lookup') && !f.table_name) {
        this.$toasted.show(`action ${f.action} 需要指定 table_name`, { type: 'error' })
        return
      }
      if (!f.router_id) {
        this.$toasted.show('请选择路由器', { type: 'error' })
        return
      }

      this.submitting = true
      try {
        let res
        if (this.formModal.isEdit) {
          res = await this.$axios.post(`user/routing/rule/policies/${this.formModal.editId}/edit`, f)
        } else {
          res = await this.$axios.post('user/routing/rule/policies', f)
        }
        if (res.data.code === 200) {
          this.$toasted.show(this.formModal.isEdit ? '已保存并下发' : '已创建并下发', { type: 'success' })
          this.formModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '操作失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.submitting = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    async togglePolicy(item) {
      try {
        const res = await this.$axios.post(`user/routing/rule/policies/${item.id}/toggle`)
        if (res.data.code === 200) {
          item.enabled = res.data.data.enabled
          this.$toasted.show('已切换并下发', { type: 'success' })
        } else {
          this.$toasted.show(res.data.msg || '操作失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    async deletePolicy(item) {
      this.showConfirm('确认删除', `确认删除策略 "${item.name}"？将同时从路由器移除该规则。`, async () => {
        try {
          const res = await this.$axios.delete(`user/routing/rule/policies/${item.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除并从路由器移除', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    }
  }
}
</script>

<style scoped>
.table >>> td,
.table >>> th {
  vertical-align: middle !important;
}
.table >>> td.text-center,
.table >>> th[style*="text-align:center"] {
  text-align: center !important;
}
</style>
